import { useCallback } from 'react';
import { SearchSelect } from '@wundo/ui';
import { listUsers } from '@/lib/users.ts';
import { userOptionLabel, type UserOption } from './userOption.ts';

interface UserPickerBaseProps {
  /** Search only inside one organisation. */
  organisationId?: string;
  /** Search only among one cohort's participants. */
  cohortId?: string;
  placeholder?: string;
  /** Put on the text field, so a `<label htmlFor>` names it. */
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  disabled?: boolean;
  clearable?: boolean;
  invalid?: boolean;
}

interface UserPickerSingleProps extends UserPickerBaseProps {
  multiple?: false;
  value: UserOption | null;
  onChange: (value: UserOption | null) => void;
}

interface UserPickerMultipleProps extends UserPickerBaseProps {
  multiple: true;
  value: UserOption[];
  onChange: (value: UserOption[]) => void;
}

export type UserPickerProps = UserPickerSingleProps | UserPickerMultipleProps;

/**
 * Pick a person by searching for them.
 *
 * The list is the `/users` search endpoint, so it is the same set of people
 * every user page lists and the same filters narrow it. It is never held in
 * full: an organisation's directory is far longer than a dropdown.
 */
export function UserPicker({ organisationId, cohortId, ...props }: UserPickerProps) {
  const loadOptions = useCallback(
    async (query: string, signal: AbortSignal): Promise<UserOption[]> => {
      const { data } = await listUsers(
        { query: query.trim() || undefined, organisationId, cohortId },
        { signal },
      );
      return data;
    },
    [organisationId, cohortId],
  );

  const shared = {
    loadOptions,
    getKey: (user: UserOption) => user.id,
    getLabel: userOptionLabel,
    placeholder: props.placeholder ?? 'Select user...',
    id: props.id,
    'aria-label': props['aria-label'],
    'aria-describedby': props['aria-describedby'],
    disabled: props.disabled,
    clearable: props.clearable,
    invalid: props.invalid,
    labels: { empty: 'No users found' },
  };

  if (props.multiple === true) {
    return (
      <SearchSelect<UserOption>
        {...shared}
        multiple
        value={props.value}
        onChange={props.onChange}
      />
    );
  }

  return (
    <SearchSelect<UserOption> {...shared} value={props.value} onChange={props.onChange} />
  );
}
